/* WeQuote Dev Board — card mutations. Pure ports of app.js mutation functions:
 * every function takes the features array (and args) and returns a MutResult
 * { features, toast? } — the provider persists the changed card via the API.
 * Behaviour (gates, stamps, stash/restore) is byte-for-byte the prototype's.
 * Platform change (PRD §7.2): addComment takes the real logged-in author. */
import type { Feature, Status } from './types'
import { DEVCOLS, POOLCOLS, isPool, isProgress, calcEffort } from './constants'
import { todayISO, fmtDate } from './dates'
import { checklistComplete, checklistProgress, sectionRank } from './logic'
import { inPipelineF } from './logic'

export interface MutResult { features: Feature[]; toast?: string; changedIds?: number[] }

const find = (fs: Feature[], id: number) => fs.find((x) => x.id === id)
const mapId = (fs: Feature[], id: number, fn: (f: Feature) => Feature): Feature[] =>
  fs.map((f) => (f.id === id ? fn({ ...f }) : f))
const changed = (fs: Feature[], id: number, toast?: string): MutResult =>
  ({ features: fs, toast, changedIds: [id] })

/* ── field updates ── */
export function upd(fs: Feature[], id: number, field: keyof Feature, val: any): MutResult {
  return changed(mapId(fs, id, (f) => ({ ...f, [field]: val })), id)
}
export function setDate(fs: Feature[], id: number, field: 'start' | 'deadline', val: string): MutResult {
  const f = find(fs, id)
  if (!f) return { features: fs }
  if (val) {
    if (field === 'start' && f.deadline && val > f.deadline)
      return { features: fs, toast: 'Start date can’t be after the deadline (' + fmtDate(f.deadline) + ').' }
    if (field === 'deadline' && f.start && val < f.start)
      return { features: fs, toast: 'Deadline can’t be before the start date (' + fmtDate(f.start) + ').' }
  }
  // roadmap-spec: HAND-setting a start (typing it) PINS the card, so it holds
  // that date instead of re-flowing after the previous card; several cards can
  // share a start (the Calendar stacks them; overlaps surface as conflicts).
  // Clearing the start clears the pin. Auto-set starts (moveStatus) don't pin.
  if (field === 'start') {
    return changed(mapId(fs, id, (g) => ({ ...g, start: val, pinnedStart: !!val })), id)
  }
  return changed(mapId(fs, id, (g) => ({ ...g, [field]: val })), id)
}
export function updTime(fs: Feature[], id: number, val: string): MutResult {
  return changed(mapId(fs, id, (f) => {
    const e = calcEffort(val)
    return { ...f, time: val, eff: e || f.eff }
  }), id)
}
function clamp100(val: any): number {
  let v = parseInt(val, 10)
  if (isNaN(v)) v = 0
  if (v < 0) v = 0
  if (v > 100) v = 100
  return v
}
export function updProgress(fs: Feature[], id: number, val: any): MutResult {
  return changed(mapId(fs, id, (f) => ({ ...f, progress: clamp100(val) })), id)
}
export function updDesignProgress(fs: Feature[], id: number, val: any): MutResult {
  return changed(mapId(fs, id, (f) => ({ ...f, designProgress: clamp100(val) })), id)
}
export function setRequiresDesign(fs: Feature[], id: number, val: boolean): MutResult {
  return changed(mapId(fs, id, (f) => ({ ...f, requiresDesign: !!val })), id)
}
/** Adding design progress auto-moves a Backlog card into Design (verify §34). */
export function maybeAutoDesign(fs: Feature[], id: number): MutResult {
  const f = find(fs, id)
  if (f && (f.designProgress || 0) > 0 && f.requiresDesign && isPool(f.status)) {
    const r = moveStatus(fs, id, 'design') // stamps designStart too
    return { ...r, toast: '“' + f.title + '” moved to Design in Progress.' }
  }
  return { features: fs }
}

/* ── checklist ── */
export function addCheckpoint(fs: Feature[], id: number, text: string): MutResult {
  const t = (text || '').trim()
  if (!t) return { features: fs }
  return changed(mapId(fs, id, (f) => ({ ...f, checklist: [...(f.checklist || []), { text: t, done: false }] })), id)
}
export function toggleCheckpoint(fs: Feature[], id: number, idx: number): MutResult {
  return changed(mapId(fs, id, (f) => {
    const list = (f.checklist || []).map((c, i) => (i === idx ? { ...c, done: !c.done } : c))
    return { ...f, checklist: list }
  }), id)
}
export function removeCheckpoint(fs: Feature[], id: number, idx: number): MutResult {
  return changed(mapId(fs, id, (f) => {
    const list = (f.checklist || []).slice()
    list.splice(idx, 1)
    return { ...f, checklist: list }
  }), id)
}

/* ── comments (author = the REAL logged-in agent) ── */
export function addComment(fs: Feature[], id: number, author: string, text: string): MutResult {
  const t = ('' + text).trim()
  if (!t) return { features: fs }
  return changed(mapId(fs, id, (f) => ({
    ...f,
    comments: [...(f.comments || []), { author, text: t, ts: new Date().toISOString() }],
  })), id)
}

/* ── links ── */
export function normalizeUrl(url: string): string {
  url = ('' + url).trim()
  if (url && !/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) url = 'https://' + url
  return url
}
export function addLink(fs: Feature[], id: number, label: string, url: string): MutResult {
  if (!url.trim()) return { features: fs, toast: 'Enter a URL for the link.' }
  const u = normalizeUrl(url)
  return changed(mapId(fs, id, (f) => ({ ...f, links: [...(f.links || []), { label: label.trim(), url: u }] })), id)
}
export function editLink(fs: Feature[], id: number, idx: number, field: 'label' | 'url', value: string): MutResult {
  return changed(mapId(fs, id, (f) => {
    const links = (f.links || []).slice()
    if (!links[idx]) return f
    let v = value
    if (field === 'url') {
      v = normalizeUrl(value)
      if (!v) return f // ignore blanking the URL entirely
    } else {
      v = ('' + value).trim()
    }
    links[idx] = { ...links[idx], [field]: v }
    return { ...f, links }
  }), id)
}
export function removeLink(fs: Feature[], id: number, idx: number): MutResult {
  return changed(mapId(fs, id, (f) => {
    const links = (f.links || []).slice()
    links.splice(idx, 1)
    return { ...f, links }
  }), id)
}

/* ── the status engine (gates + side effects; PRD §5.1/§5.2/§5.8) ── */
export function moveStatus(fs: Feature[], id: number, newStatus: Status): MutResult {
  const target = find(fs, id)
  if (target && (newStatus === 'design' || newStatus === 'designcomplete') && !target.requiresDesign) {
    return { features: fs, toast: '“' + target.title + '” is marked as not needing design, so it can’t enter a design column. Tick “Design required” first.' }
  }
  if (target && newStatus === 'live' && !checklistComplete(target)) {
    const cp = checklistProgress(target)
    return { features: fs, toast: '“' + target.title + '” still has checkpoints to complete (' + cp.done + '/' + cp.total + '). Tick them all before moving to Live.' }
  }
  const next = mapId(fs, id, (f) => {
    const old = f.status
    if (old === newStatus) return f
    const wasProg = isProgress(old)
    const willProg = isProgress(newStatus)

    // Entering Design in Progress: stamp the design start date once
    if (newStatus === 'design' && !f.designStart) f.designStart = todayISO()
    // Design Complete: stamp; clear only when pushed back before Design Complete
    if (newStatus === 'designcomplete' && !f.designCompletedOn) f.designCompletedOn = todayISO()
    else if (isPool(newStatus) || newStatus === 'design') f.designCompletedOn = ''

    // Moving forward into a dev column
    if (DEVCOLS.indexOf(newStatus) > -1) {
      f.dev = newStatus // assigned dev follows the dev column
      f.onhold = false
      if (f.stash) { // returning: restore prior progress state
        f.progress = f.stash.progress
        f.pnotes = f.stash.pnotes
        if (f.stash.start) f.start = f.stash.start
        f.stash = null
      }
      if (!f.start) f.start = todayISO() // auto-set start date on entry
    }
    // Moving back out of the dev pipeline into a pre-dev column: stash progress
    if (wasProg && !willProg && (isPool(newStatus) || newStatus === 'design' || newStatus === 'designcomplete')) {
      f.stash = { progress: f.progress, pnotes: f.pnotes, start: f.start }
    }
    if (newStatus === 'onhold') {
      f.onhold = true
      if (!f.prevStatus || old !== 'onhold') f.prevStatus = old
    } else {
      f.onhold = false
    }
    // Actual Completion Date: auto-stamp on LIVE; clear when it leaves
    if (newStatus === 'live') f.completedOn = todayISO()
    else if (old === 'live') f.completedOn = ''

    f.status = newStatus
    return f
  })
  return changed(next, id)
}

/** Mark-as-Live quick action (verify §66) — the view confirms; this enforces the gate. */
export function markLive(fs: Feature[], id: number): MutResult {
  const f = find(fs, id)
  if (!f || f.status === 'live') return { features: fs }
  return moveStatus(fs, id, 'live')
}

export function placeOnHold(fs: Feature[], id: number): MutResult {
  return changed(mapId(fs, id, (f) => ({ ...f, prevStatus: f.status, status: 'onhold', onhold: true })), id)
}
export function resumeCard(fs: Feature[], id: number): MutResult {
  return changed(mapId(fs, id, (f) => {
    let target: string
    if (f.dev && DEVCOLS.indexOf(f.dev) > -1) target = f.dev // back to the assigned dev column
    else if (f.prevStatus && f.prevStatus !== 'onhold') target = f.prevStatus
    else target = 'designcomplete'
    return { ...f, status: target, onhold: false }
  }), id)
}

/* ── v2 §1: move a card into a pool band — drops to the BOTTOM of that band,
 * numbers re-flow (they run continuously Now → Next → Later). The live
 * click-to-edit status control routes pool picks here (v2 §6 decision 1). ── */
export function moveToBand(fs: Feature[], id: number, band: string): MutResult {
  if (POOLCOLS.indexOf(band) < 0) return { features: fs }
  const f = find(fs, id)
  if (!f) return { features: fs }
  const same = fs.filter((x) => x.id !== id && x.status === band)
  const po = same.length ? Math.max(...same.map((x) => x.po)) + 1 : f.po
  const next = mapId(fs, id, (g) => ({ ...g, status: band, onhold: false, po }))
  return changed(next, id)
}

/* ── priority position (pop-up "Priority Position" select) ── */
export function moveToPosition(fs: Feature[], id: number, pos: number): MutResult {
  // v2 §6 decision 2: the position dropdown reorders WITHIN the card's band —
  // bands stay contiguous; changing band happens via the status control.
  pos = Math.trunc(pos)
  if (!pos) return { features: fs }
  const me = find(fs, id)
  if (!me) return { features: fs }
  const pipeline = fs.filter(inPipelineF).sort((a, b) => sectionRank(a) - sectionRank(b) || a.po - b.po)
  const rest = fs.filter((f) => !inPipelineF(f)).sort((a, b) => a.po - b.po)
  const fi = pipeline.findIndex((f) => f.id === id)
  if (fi < 0) return { features: fs }
  const lo = pipeline.findIndex((f) => sectionRank(f) === sectionRank(me))
  let hi = lo
  while (hi + 1 < pipeline.length && sectionRank(pipeline[hi + 1]) === sectionRank(me)) hi++
  if (pos < lo + 1) pos = lo + 1
  if (pos > hi + 1) pos = hi + 1
  const moved = pipeline.splice(fi, 1)[0]
  pipeline.splice(pos - 1, 0, moved)
  const all = pipeline.concat(rest)
  const withPo = all.map((f, i) => ({ ...f, po: i + 1 }))
  const changedIds = withPo.filter((f) => { const b = fs.find((x) => x.id === f.id); return b && b.po !== f.po }).map((f) => f.id)
  return { features: withPo, changedIds }
}

/* ── requests / chases (pop-up actions) ── */
/** crm (optional, Lee 28 Aug): when the company was matched to a real CRM
 *  record, the request quietly carries {crmId, crmKind} so the future
 *  "requests on the customer's CRM page" view needs no name-matching. */
export function addRequest(fs: Feature[], id: number, company: string, person: string, date: string,
  crm?: { id: number | string; kind: 'paid' | 'deal' }): MutResult {
  const c = (company || '').trim()
  if (!c) return { features: fs, toast: 'Please enter the company that requested this.' }
  const rec: any = { company: c, person: (person || '').trim(), date: date || todayISO() }
  if (crm && crm.id != null) { rec.crmId = crm.id; rec.crmKind = crm.kind }
  const next = mapId(fs, id, (f) => ({
    ...f,
    requests: [...(f.requests || []), rec],
  }))
  return { ...changed(next, id), toast: 'Request logged for ' + c + '.' }
}
export function logChaseAppend(fs: Feature[], id: number, company: string, person: string, date: string): MutResult {
  const next = mapId(fs, id, (f) => ({
    ...f,
    requests: [...(f.requests || []), { company: company || '', person: person || '', date: date || todayISO() }],
  }))
  return { ...changed(next, id), toast: 'Chase logged for ' + (company || '') + '.' }
}

/* ── archive / restore (reason required; PRD §5.9) ── */
export function confirmArchive(fs: Feature[], id: number, reason: string): MutResult {
  const r = (reason || '').trim()
  if (!r) return { features: fs, toast: 'Please type a reason.' }
  let title = ''
  const next = mapId(fs, id, (f) => {
    title = f.title
    return { ...f, prevStatus: f.status, status: 'archived', onhold: false, archiveReason: r }
  })
  return { ...changed(next, id), toast: '“' + title + '” archived.' }
}
export function unarchive(fs: Feature[], id: number): MutResult {
  const next = mapId(fs, id, (f) => ({
    ...f,
    status: f.prevStatus && f.prevStatus !== 'archived' ? f.prevStatus : 'needsreview',
    archiveReason: '',
  }))
  return { ...changed(next, id), toast: 'Restored from archive.' }
}
